'use client'

import { useState } from "react"
import Link from "next/link"
import { ArrowUpRight, Pause, Play } from "lucide-react"

export type BannerSlide = {
  id: string
  src: string
  blurDataURL?: string | null
  width?: number | null
  height?: number | null
  alt?: string
  href?: string | null
}

// What <img> falls back to when a `loadFrom` media query doesn't match, so the
// device never downloads a banner it can't see.
const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

// Local join instead of cn from @/lib/util, which would pull moment and UI
// helpers into the login page bundle for a few class names.
const cx = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(' ')

interface BannerSliderProps {
  slides: BannerSlide[]
  /** Time per slide. Drives the dot fill (globals.css .banner-progress). */
  intervalMs: number
  /** Media query the images may load under, e.g. '(min-width: 1024px)' on the
      auth page where the column is hidden on phones. Omit to load everywhere. */
  loadFrom?: string
  /** Rounded parent provides the shape; this fills it (absolute inset-0). */
  className?: string
}

/*
  Shared by the auth page banner column and the vendor dashboard carousel.

  Loading: only the first slide is in the server HTML; slide n+1 mounts after
  slide n has fully loaded, so nothing competes with the first paint. Each slide
  paints its ~20px blurDataURL immediately and fades the real image in once
  loaded.

  Rotation has no timer: the active dot's fill is a CSS animation and its
  animationend advances the slide. Pausing freezes that animation, a background
  tab stops it, and prefers-reduced-motion hides the fill (and the pause
  button), leaving the dots for manual navigation. Rotation starts once the
  first banner has loaded.
*/
export default function BannerSlider({ slides, intervalMs, loadFrom, className }: BannerSliderProps) {
  const [view, setView] = useState({ current: 0, previous: -1 })
  const [loaded, setLoaded] = useState<Record<string, true>>({})
  const [paused, setPaused] = useState(false)
  const count = slides.length
  const { current, previous } = view
  const started = !!loaded[slides[0]?.id]
  const active = slides[current]

  // Furthest slide allowed to download: one past the last contiguous loaded one.
  let mountUpTo = 0
  while (mountUpTo < count - 1 && loaded[slides[mountUpTo].id]) mountUpTo++

  const goTo = (index: number) => setView(v => v.current === index ? v : { current: index, previous: v.current })
  const advance = () => setView(v => ({ current: (v.current + 1) % count, previous: v.current }))

  const markLoaded = (id: string, img: HTMLImageElement | null) => {
    // The fallback pixel also fires load; only the real banner counts.
    if (!img || !img.complete || !img.naturalWidth || img.currentSrc.startsWith('data:')) return
    setLoaded(prev => prev[id] ? prev : { ...prev, [id]: true })
  }

  return (
    <div className={cx("absolute inset-0 overflow-hidden", className)}>
      {slides.map((slide, i) => {
        const isCurrent = i === current
        // The outgoing slide stays opaque underneath while the next fades in on
        // top, so the crossfade never dips to the background.
        const visible = isCurrent || i === previous
        const isLoaded = !!loaded[slide.id]
        const image = (i <= mountUpTo || visible) && (
          <>
            {loadFrom ? (
              <picture>
                <source media={loadFrom} srcSet={slide.src} />
                <SlideImage slide={slide} src={EMPTY_IMAGE} priority={i === 0} loaded={isLoaded} onLoaded={img => markLoaded(slide.id, img)} />
              </picture>
            ) : (
              <SlideImage slide={slide} src={slide.src} priority={i === 0} loaded={isLoaded} onLoaded={img => markLoaded(slide.id, img)} />
            )}
          </>
        )
        return (
          <div
            key={slide.id}
            aria-hidden={!isCurrent}
            // Hidden slides must not take focus or clicks (their links included).
            inert={!isCurrent}
            className={cx(
              "absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none",
              isCurrent ? "z-20" : i === previous ? "z-10" : "z-0"
            )}
            style={{ opacity: visible ? 1 : 0 }}
          >
            {slide.blurDataURL && (
              <div
                className="absolute inset-0 scale-110 bg-cover bg-center blur-2xl"
                style={{ backgroundImage: `url(${slide.blurDataURL})` }}
              />
            )}
            {slide.href ? (
              <Link href={slide.href} className="absolute inset-0 block" aria-label={slide.alt || 'Баннер'}>
                {image}
              </Link>
            ) : image}
          </div>
        )
      })}

      {/* Bottom bar: link pill (left), dots (centre), pause (right). */}
      {active?.href && (
        <Link
          href={active.href}
          className="absolute bottom-5 left-5 z-30 hidden h-9 items-center gap-1 rounded-full bg-white px-3.5 text-[13px] font-semibold text-zinc-900 font-gilroy outline-none transition-colors hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 md:inline-flex"
        >
          Дэлгэрэнгүй
          <ArrowUpRight className="size-3.5" />
        </Link>
      )}

      {count > 1 && (
        <>
          <div className="absolute bottom-5 left-1/2 z-30 flex h-9 -translate-x-1/2 items-center rounded-full bg-black/25 px-2 backdrop-blur-md">
            {slides.map((slide, i) => {
              const isActive = i === current
              return (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`${i + 1}-р баннер`}
                  aria-current={isActive}
                  className="group flex h-full items-center px-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  <span
                    className={cx(
                      "relative block h-1.5 overflow-hidden rounded-full transition-[width,background-color] duration-300 ease-out motion-reduce:transition-none",
                      isActive ? "w-6 bg-white/35 motion-reduce:bg-white" : "w-1.5 bg-white/55 group-hover:bg-white/90"
                    )}
                  >
                    {isActive && started && (
                      <span
                        key={current}
                        // Timing lives in .banner-progress (globals.css); see the note there.
                        className="banner-progress absolute inset-0 origin-left rounded-full bg-white motion-reduce:hidden"
                        style={{
                          ['--banner-slide-ms' as string]: `${intervalMs}ms`,
                          animationPlayState: paused ? 'paused' : 'running',
                        }}
                        onAnimationEnd={advance}
                      />
                    )}
                  </span>
                </button>
              )
            })}
          </div>

          <button
            type="button"
            onClick={() => setPaused(p => !p)}
            aria-label={paused ? 'Үргэлжлүүлэх' : 'Түр зогсоох'}
            title={paused ? 'Үргэлжлүүлэх' : 'Түр зогсоох'}
            className="absolute bottom-5 right-5 z-30 flex size-9 items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-md transition-colors hover:bg-black/40 outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:hidden"
          >
            {paused ? <Play className="size-3.5 fill-current" /> : <Pause className="size-3.5 fill-current" />}
          </button>
        </>
      )}
    </div>
  )
}

function SlideImage({ slide, src, priority, loaded, onLoaded }: { slide: BannerSlide, src: string, priority: boolean, loaded: boolean, onLoaded: (img: HTMLImageElement | null) => void }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={slide.alt || ''}
      width={slide.width ?? undefined}
      height={slide.height ?? undefined}
      fetchPriority={priority ? 'high' : 'low'}
      decoding="async"
      // Covers an image that finished before hydration attached onLoad.
      ref={onLoaded}
      onLoad={e => onLoaded(e.currentTarget)}
      className="absolute inset-0 size-full object-cover transition-opacity duration-500 ease-out motion-reduce:transition-none"
      style={{ opacity: loaded ? 1 : 0 }}
    />
  )
}
